import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Descriptions, Modal, Space, Spin, Switch, Tag, Typography } from "antd";
import { ExperimentOutlined, ReloadOutlined, WarningOutlined } from "@ant-design/icons";
import InfoAyuda, { AyudaBloque } from "../../../Components/InfoAyuda";
import { botAsistenteApi, FRASE_MODO_REAL } from "../../../services/botAsistente.api";
import type { BotEstado } from "../../../interfaces/botAsistente.interfaces";
import ConfirmacionEscrita from "./ConfirmacionEscrita";

const { Text } = Typography;

export const MOTIVO_SOLO_SUPERADMIN = "Solo el superadministrador puede cambiar esto.";
/** Por qué no se puede confirmar «Encender» cuando la última lectura del modo falló. */
export const MOTIVO_DESACTUALIZADO =
  "No se pudo comprobar el modo hace un momento y pudo haber cambiado. Cierra este diálogo, toca «Actualizar» y vuelve a intentarlo.";

/** La etiqueta de AntD no parte líneas: sin esto, «REAL: lo que entra por WhatsApp SE GUARDA» desbordaría la pantalla a 360 px. */
const ETIQUETA_GRANDE: React.CSSProperties = {
  fontSize: 14,
  padding: "2px 10px",
  margin: 0,
  whiteSpace: "normal",
  height: "auto",
  lineHeight: 1.5,
  wordBreak: "break-word",
};

export interface EstadoModoSeccionProps {
  esSuperadmin: boolean;
  /** `null` = todavía no se leyó o no se pudo leer: el interruptor queda bloqueado, nunca se adivina. */
  modoReal: boolean | null;
  cargando: boolean;
  errorModo: boolean;
  /**
   * La última lectura de fondo (la de cada 45 s) falló y lo que se ve es lo último que se supo: el modo puede haber cambiado.
   * Mientras dure, el interruptor queda bloqueado (no se cambia nada a ciegas) y se dice por qué.
   */
  desactualizado?: boolean;
  estado: BotEstado | null;
  errorEstado: boolean;
  onReintentar: () => void;
  /** Se llama después de CADA intento de cambio (haya salido bien o no): el contenedor vuelve a leer el modo del servidor. */
  onCambioTerminado: () => void | Promise<void>;
}

/**
 * (a) Estado y modo. Muestra con color y con palabras si lo que entra por WhatsApp se guarda de verdad, y deja que el
 * superadministrador lo cambie. El interruptor está CONTROLADO por lo que dijo el servidor: un clic solo abre la
 * confirmación y, pase lo que pase con la petición (incluido un 403), el interruptor queda en lo que el servidor diga
 * al volver a leer.
 */
const EstadoModoSeccion: React.FC<EstadoModoSeccionProps> = ({
  esSuperadmin,
  modoReal,
  cargando,
  errorModo,
  desactualizado = false,
  estado,
  errorEstado,
  onReintentar,
  onCambioTerminado,
}) => {
  const [confirmandoEncender, setConfirmandoEncender] = useState(false);
  const [confirmandoApagar, setConfirmandoApagar] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  /** El modo cambió por fuera mientras había un diálogo abierto: el diálogo se cerró y esto lo explica (no se pide repetir nada). */
  const [dialogoCerrado, setDialogoCerrado] = useState<"encendido" | "apagado" | null>(null);
  const enCurso = useRef(false);

  // El aviso «ya estaba ENCENDIDO/APAGADO» cuenta cómo estaba el modo cuando se cerró el diálogo. Si después el modo vuelve a cambiar (otra persona
  // lo apagó de nuevo, o lo encendió), el aviso dejaría de ser verdad y seguiría ahí diciendo lo contrario de lo que muestra la etiqueta de arriba:
  // se quita. Mientras el modo no se conoce (`null`) no hay nada con qué compararlo y se deja.
  useEffect(() => {
    if (modoReal === null) return;
    setDialogoCerrado((aviso) => (aviso === "encendido" && !modoReal) || (aviso === "apagado" && modoReal) ? null : aviso);
  }, [modoReal]);

  // Un diálogo abierto describe el modo de cuando se abrió. Si otra persona lo cambió en el medio (por ejemplo, otro superadministrador
  // ya lo encendió), su texto («Desde este momento… quedará guardado») dejó de ser verdad y confirmar mandaría un cambio repetido: se
  // cierra y se explica. No corre mientras ESTA pantalla está aplicando un cambio propio (el modo que llega entonces es el que se pidió).
  useEffect(() => {
    if (aplicando) return;
    if (modoReal === true && confirmandoEncender) {
      setConfirmandoEncender(false);
      setDialogoCerrado("encendido");
    } else if (modoReal === false && confirmandoApagar) {
      setConfirmandoApagar(false);
      setDialogoCerrado("apagado");
    }
  }, [modoReal, confirmandoEncender, confirmandoApagar, aplicando]);

  const aplicar = async (activar: boolean) => {
    if (enCurso.current) return;
    enCurso.current = true;
    setAplicando(true);
    try {
      // Se manda la frase canónica: lo que la persona escribió solo fue la compuerta (ConfirmacionEscrita).
      await botAsistenteApi.cambiarModoReal(activar, activar ? FRASE_MODO_REAL : undefined);
    } finally {
      // Con éxito o sin él (403, red caída…) el modo se vuelve a leer del servidor: nunca se da por hecho lo local.
      setConfirmandoEncender(false);
      setConfirmandoApagar(false);
      await onCambioTerminado();
      enCurso.current = false;
      setAplicando(false);
    }
  };

  // El interruptor solo se dibuja cuando el modo se conoce (abajo, la rama de «no se pudo leer» no lo muestra), así que aquí
  // no hace falta volver a preguntarlo. Sí se bloquea si lo último que se supo ya es viejo (`desactualizado`).
  const cambiable = esSuperadmin && !desactualizado && !aplicando;

  const alAccionar = () => {
    if (!cambiable) return;
    setDialogoCerrado(null);
    if (modoReal) setConfirmandoApagar(true);
    else setConfirmandoEncender(true);
  };

  return (
    <Card
      size="small"
      data-testid="asistente-seccion-estado"
      title={
        <Space size={4}>
          <span>Estado y modo</span>
          <InfoAyuda titulo="Estado y modo" testId="ayuda-estado">
            <AyudaBloque titulo="¿Qué es el modo real?">
              Es el interruptor que decide si lo que la gente le escribe al asistente por WhatsApp se guarda de verdad en
              los gastos de la empresa. Está apagado mientras se hacen las pruebas.
            </AyudaBloque>
            <AyudaBloque titulo="Con el modo real apagado (simulación)">
              El asistente revisa cada gasto igual que uno real, pero no lo guarda en ninguna tabla real. Solo anota lo que
              habría guardado en la lista de «Simulaciones», que se puede vaciar cuando quieras.
            </AyudaBloque>
            <AyudaBloque titulo="¿Quién puede cambiarlo?">
              Solo el superadministrador. Para encenderlo debe escribir una frase de confirmación. Cada cambio queda
              registrado.
            </AyudaBloque>
          </InfoAyuda>
        </Space>
      }
      extra={
        <Button size="small" icon={<ReloadOutlined />} onClick={onReintentar} data-testid="estado-actualizar">
          Actualizar
        </Button>
      }
    >
      {dialogoCerrado !== null && (
        <Alert
          type="info"
          showIcon
          closable
          onClose={() => setDialogoCerrado(null)}
          style={{ marginBottom: 12 }}
          data-testid="estado-dialogo-cerrado"
          message={
            dialogoCerrado === "encendido"
              ? "El modo real ya estaba ENCENDIDO: lo cambió otra persona mientras confirmabas. No hace falta repetirlo."
              : "El modo real ya estaba APAGADO: lo cambió otra persona mientras confirmabas. No hace falta repetirlo."
          }
        />
      )}
      {cargando && modoReal === null && !errorModo ? (
        <div style={{ textAlign: "center", padding: 24 }} data-testid="estado-cargando">
          <Spin />
        </div>
      ) : errorModo || modoReal === null ? (
        <Alert
          type="error"
          showIcon
          data-testid="estado-error"
          message="No se pudo leer el modo actual del asistente"
          description="Mientras no se sepa, el interruptor queda bloqueado para no cambiar nada a ciegas. Revisa tu conexión e inténtalo de nuevo."
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={onReintentar}>
              Reintentar
            </Button>
          }
        />
      ) : (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
            {modoReal ? (
              <Tag color="red" icon={<WarningOutlined />} style={ETIQUETA_GRANDE} data-testid="estado-modo">
                REAL: lo que entra por WhatsApp SE GUARDA
              </Tag>
            ) : (
              <Tag color="blue" icon={<ExperimentOutlined />} style={ETIQUETA_GRANDE} data-testid="estado-modo">
                Simulación (nada se guarda)
              </Tag>
            )}
          </div>

          {modoReal && (
            <Alert
              type="error"
              showIcon
              data-testid="estado-alerta-real"
              message="El modo real está ENCENDIDO"
              description="Los gastos que se registren por WhatsApp quedan guardados de verdad en las finanzas de la empresa. Si no es lo que quieres, apágalo ahora."
            />
          )}

          {desactualizado && (
            <Alert
              type="warning"
              showIcon
              data-testid="estado-desactualizado"
              message="No se pudo comprobar el modo hace un momento"
              description="Lo que ves es lo último que se supo y pudo haber cambiado. Mientras tanto el interruptor queda bloqueado: toca «Actualizar»."
            />
          )}

          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
            <Switch
              checked={modoReal}
              disabled={!cambiable}
              loading={aplicando}
              onChange={alAccionar}
              aria-label="Modo real del asistente de WhatsApp"
              data-testid="interruptor-modo-real"
            />
            <Text strong>Modo real</Text>
            {!esSuperadmin && (
              <Text type="secondary" data-testid="modo-real-solo-superadmin">
                {MOTIVO_SOLO_SUPERADMIN}
              </Text>
            )}
            {esSuperadmin && desactualizado && (
              <Text type="secondary" data-testid="modo-real-bloqueado-desactualizado">
                Bloqueado hasta poder comprobar el modo actual.
              </Text>
            )}
          </div>

          {errorEstado ? (
            <Text type="secondary" data-testid="estado-resumen-error">
              No se pudo leer el resumen de lo que el asistente guardó.
            </Text>
          ) : estado ? (
            <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered data-testid="estado-resumen">
              <Descriptions.Item label="Gastos guardados de verdad por el asistente">
                <Text strong>{estado.gastosDelBot}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Operaciones reales">{estado.operacionesReales}</Descriptions.Item>
            </Descriptions>
          ) : null}
        </Space>
      )}

      <ConfirmacionEscrita
        abierto={confirmandoEncender}
        titulo="Encender el modo real"
        frase={FRASE_MODO_REAL}
        textoConfirmar="Encender modo real"
        cargando={aplicando}
        // La lectura de fondo falló con el diálogo abierto: lo que se ve puede ser viejo y encender sobre eso sería a ciegas (el interruptor ya se bloquea igual).
        bloqueadoPor={desactualizado ? MOTIVO_DESACTUALIZADO : undefined}
        onConfirmar={() => aplicar(true)}
        onCancelar={() => setConfirmandoEncender(false)}
        testId="confirmar-modo-real"
      >
        <p style={{ marginTop: 0 }}>
          <b>Desde este momento, lo que se registre por WhatsApp quedará guardado de verdad</b> en los gastos de la
          empresa, y contará en las finanzas.
        </p>
        <p style={{ marginBottom: 0 }}>
          Úsalo solo cuando las pruebas hayan terminado. Puedes volver a simulación cuando quieras; lo que se guardó
          mientras tanto no se borra solo.
        </p>
      </ConfirmacionEscrita>

      <Modal
        open={confirmandoApagar}
        title="Volver a simulación"
        okText="Volver a simulación"
        cancelText="Cancelar"
        confirmLoading={aplicando}
        onOk={() => void aplicar(false)}
        onCancel={() => {
          if (!aplicando) setConfirmandoApagar(false);
        }}
        maskClosable={false}
        destroyOnClose
        style={{ maxWidth: "calc(100vw - 24px)", top: 24 }}
        data-testid="confirmar-modo-simulacion"
      >
        <p style={{ margin: 0 }}>
          El asistente dejará de guardar gastos de verdad: todo lo que entre por WhatsApp pasará a ser una simulación. Lo
          que ya se guardó mientras el modo real estuvo encendido se queda como está.
        </p>
      </Modal>
    </Card>
  );
};

export default EstadoModoSeccion;
